import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, LoadScript, Polyline } from '@react-google-maps/api';
import { Map } from 'immutable';
import env from 'lib/env';
import Search from 'models/search';
import { Location } from 'models/locations';

const { GOOGLE_MAPS_API_KEY } = env();
const DEFAULT_INITIAL_ZOOM = 16;

export class GoogleMapView extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.state = {
      zoom: props.currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM,
    };
    this.triggerBoundsChange = this.triggerBoundsChange.bind(this);
    this.fitBoundsToResults = this.fitBoundsToResults.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!this.map) return;

    const { currentSearch, locations, selectedLocation } = this.props;
    const prevSearch = prevProps.currentSearch;

    const destinationChanged = currentSearch.destination !== prevSearch.destination;
    const parkingTypeChanged = currentSearch.parkingType !== prevSearch.parkingType;
    const selectionChanged = selectedLocation !== prevProps.selectedLocation;
    const initialLoad = (!prevProps.locations || prevProps.locations.size === 0) && (locations && locations.size > 0);

    if (destinationChanged || parkingTypeChanged || selectionChanged || initialLoad) {
      this.fitBoundsToResults();
    }
  }

  fitBoundsToResults() {
    if (typeof window === 'undefined' || !this.map || !window.google) return;

    const { currentSearch, locations, selectedLocation } = this.props;
    const bounds = new window.google.maps.LatLngBounds();

    const currentLat = currentSearch.anchorLat;
    const currentLng = currentSearch.anchorLng;

    // 1. Include search destination / anchor
    bounds.extend({ lat: currentLat, lng: currentLng });

    if (selectedLocation) {
      // 2. Fit to selected location entrance + search destination
      const entrance = selectedLocation.entrances.first();
      if (entrance) {
        bounds.extend({ lat: entrance.lat, lng: entrance.lng });
      }
    } else if (locations && locations.size > 0) {
      // 3. Include nearby search results (limit to first 3 within half a mile for ideal viewport zoom)
      let count = 0;
      locations.sortBy(l => l.distance).forEach((location) => {
        // Enforce max 3 locations, and none further than 2640 feet (half a mile) to prevent excessive zooming out
        const isIdeal = count < 3 && (count === 0 || location.distance < 2640);
        if (!isIdeal) return;

        const entrance = location.entrances.get(0);
        if (entrance) {
          bounds.extend({ lat: entrance.lat, lng: entrance.lng });
          count++;
        }
      });
    }

    // Call fitBounds with Mapbox-aligned asymmetrical padding
    const padding = selectedLocation
      ? { top: 100, bottom: 25, left: 50, right: 100 }
      : { top: 75, bottom: 25, left: 25, right: 100 };

    const maxZoom = selectedLocation ? 18 : 17;
    this.map.setOptions({ maxZoom });

    this.map.fitBounds(bounds, padding);

    // Reset maxZoom once the camera transition is complete so the user can zoom manually
    window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
      if (this.map) {
        this.map.setOptions({ maxZoom: null });
      }
    });
  }

  triggerBoundsChange() {
    if (!this.map) return;

    const zoomLevel = this.map.getZoom();
    if (this.state.zoom !== zoomLevel) {
      this.setState({ zoom: zoomLevel });
    }

    if (this.props.selectedLocation) return;

    const { currentSearch, boundsChange } = this.props;
    const bounds = this.map.getBounds();
    const center = this.map.getCenter();

    if (!bounds || !center) return;

    const lat = center.lat();
    const lng = center.lng();

    // Avoid infinite loops by skipping updates if coordinates are unchanged
    const searchLat = currentSearch.lat || currentSearch.anchorLat;
    const searchLng = currentSearch.lng || currentSearch.anchorLng;
    const searchZoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    const latDiff = Math.abs(lat - searchLat);
    const lngDiff = Math.abs(lng - searchLng);
    const zoomDiff = Math.abs(zoomLevel - searchZoom);

    if (latDiff < 0.0001 && lngDiff < 0.0001 && zoomDiff < 0.1) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const mockBounds = {
      getNorthEast: () => ({ lat: ne.lat(), lng: ne.lng() }),
      getSouthWest: () => ({ lat: sw.lat(), lng: sw.lng() }),
    };

    boundsChange({
      bounds: mockBounds,
      lat,
      lng,
      zoomLevel,
      router: false,
    });
  }

  renderStreetParkingPolylines() {
    const { currentSearch, streetParking, showStreetParking, selectedLocation } = this.props;
    if (
      currentSearch.isMonthlySearch ||
      !showStreetParking ||
      selectedLocation ||
      !streetParking ||
      streetParking.size === 0
    ) {
      return null;
    }

    const { zoom } = this.state;
    const strokeWeight = zoom > 16 ? 4 : (zoom < 16 ? 1 : 2);

    return streetParking.map((segment) => {
      const path = segment.geometries.map(g => ({ lat: g.lat, lng: g.lng })).toArray();
      return (
        <Polyline
          key={segment.id}
          path={path}
          options={{
            strokeColor: segment.color,
            strokeWeight,
            strokeOpacity: 1.0,
          }}
        />
      );
    }).toArray();
  }

  render() {
    const { currentSearch } = this.props;
    const lat = currentSearch.lat || currentSearch.anchorLat;
    const lng = currentSearch.lng || currentSearch.anchorLng;
    const zoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    return (
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          defaultCenter={{ lat, lng }}
          defaultZoom={zoom}
          onLoad={(map) => { 
            this.map = map; 
            this.fitBoundsToResults();
          }}
          onDragEnd={() => this.triggerBoundsChange()}
          onZoomChanged={() => this.triggerBoundsChange()}
          options={{
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            zoomControl: true,
            zoomControlOptions: {
              position: (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.ControlPosition) ? window.google.maps.ControlPosition.TOP_RIGHT : 3,
            },
          }}
        >
          {this.props.children}
          {this.renderStreetParkingPolylines()}
        </GoogleMap>
      </LoadScript>
    );
  }
}

GoogleMapView.propTypes = {
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  selectedLocation: PropTypes.instanceOf(Location),
  locations: PropTypes.any,
  boundsChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  streetParking: PropTypes.instanceOf(Map),
  showStreetParking: PropTypes.bool,
};

GoogleMapView.defaultProps = {
  selectedLocation: null,
  locations: null,
  children: null,
  streetParking: null,
  showStreetParking: false,
};

export default GoogleMapView;
